'use client';

/**
 * CookieConsentV2 — bespoke, V2-skinned cookie consent.
 *
 * Replaces `vanilla-cookieconsent` with a fully controlled React
 * implementation that:
 *   - Matches the V2 (Linear-light) design system.
 *   - Speaks every locale we ship (en, es, de, fr, it).
 *   - Exposes four categories — necessary / functional / analytics /
 *     marketing — so we can layer Brevo/retargeting later without
 *     touching the consent layer again.
 *   - Stays backwards-compatible with `ConditionalAnalytics` by
 *     reusing the cookie name and the `cc:onConsent` / `cc:onChange`
 *     events the old library used.
 *
 * The banner is a slim bottom bar — never a hard modal — so the page
 * stays readable while the user decides. Clicking "Customize" opens a
 * proper modal with category toggles, GDPR-style.
 */

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {motion, useReducedMotion} from 'motion/react';
import {
  CATEGORIES,
  CHANGE_EVENT,
  CONSENT_EVENT,
  OPEN_EVENT,
  acceptAll as storeAcceptAll,
  clearStoredConsent,
  getStoredConsent,
  rejectAll as storeRejectAll,
  setStoredConsent,
  syncConsentMode
} from './storage';
import {getMessagesForLocale} from './messages';

const DEFAULT_SELECTION = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false
};

function selectionFromCategories(categories) {
  if (!categories) return {...DEFAULT_SELECTION};
  return CATEGORIES.reduce((acc, key) => {
    acc[key] = key === 'necessary' ? true : categories.includes(key);
    return acc;
  }, {});
}

/* --------------------------------------------------------------------------
 * UI primitives
 * --------------------------------------------------------------------------
 * These intentionally do not import from `forms/FormPrimitives` — the
 * consent layer needs to render before any non-essential JS, so we
 * keep dependencies extremely tight.
 * ----------------------------------------------------------------------- */

/* The thumb is absolutely positioned so its placement is unaffected
 * by any user-agent padding leaking through the v2 button reset
 * (which intentionally only resets font/color/background, not
 * padding). Track is 44x24, thumb is 20x20, so the thumb is pinned
 * top:2px / left:2px and slides 20px on the X axis to land flush
 * against the right edge — symmetric 2px breathing room on both
 * sides regardless of cascade. */
function Switch({checked, onChange, disabled, label}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={[
        'relative h-6 w-11 shrink-0 rounded-full p-0 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        'focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-white',
        disabled
          ? 'cursor-not-allowed bg-[color:var(--color-border)] opacity-70'
          : checked
          ? 'bg-[var(--color-primary)]'
          : 'bg-[color:var(--color-border-strong)]'
      ].join(' ')}
    >
      <span
        aria-hidden
        className={[
          'pointer-events-none absolute top-0.5 left-0.5 size-5 rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-5' : 'translate-x-0'
        ].join(' ')}
      />
    </button>
  );
}

function CategoryRow({id, title, body, checked, disabled, onChange, alwaysOnLabel}) {
  return (
    <li className="flex items-start justify-between gap-6 border-t border-border py-5">
      <div className="min-w-0">
        <div className="flex items-center gap-3">
          <h3 className="font-display text-[15px] font-medium text-foreground">
            {title}
          </h3>
          {disabled && (
            <span className="rounded-full border border-border px-2 py-0.5 font-mono text-[10px] tracking-[0.14em] uppercase text-foreground-subtle">
              {alwaysOnLabel}
            </span>
          )}
        </div>
        <p className="mt-2 text-[13px] leading-relaxed text-foreground-subtle">
          {body}
        </p>
      </div>
      <Switch
        label={title}
        checked={checked}
        disabled={disabled}
        onChange={(next) => onChange(id, next)}
      />
    </li>
  );
}

/* --------------------------------------------------------------------------
 * Banner — bottom-left floating card, three actions, slide-up entrance.
 * ----------------------------------------------------------------------- */

/* The banner is intentionally a small, opinionated floating card pinned to the
 * bottom-right corner — not an edge-to-edge bar. The earlier full-width bar was
 * visually heavy on a content-led page (the V2 design language is whitespace,
 * hairlines, and sparing brand color), so we trade omnipresence for elegance:
 * a 400px card with the V2 modal radius, hairline border, and a tiny brand-blue
 * status dot acting as a visual anchor instead of a giant headline.
 *
 * Action hierarchy: "Accept all" is the brand pill (anchored on the right of
 * the action row, which is where the eye lands last when scanning left→right);
 * "Reject" is an outlined chip at the same prominence (GDPR-equal weighting);
 * "Customize" is a text-only ghost button on the far left for power users. */
function Banner({messages, onAcceptAll, onRejectAll, onCustomize}) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.aside
      role="region"
      aria-label={messages.banner.title}
      data-cc-banner
      initial={prefersReducedMotion ? {opacity: 0} : {opacity: 0, y: 16}}
      animate={prefersReducedMotion ? {opacity: 1} : {opacity: 1, y: 0}}
      transition={{duration: 0.45, ease: [0.16, 1, 0.3, 1]}}
      className="fixed bottom-4 right-4 z-[60] w-[calc(100vw-2rem)] max-w-[400px] sm:bottom-6 sm:right-6"
    >
      <div className="rounded-2xl border border-border bg-white/95 p-5 shadow-[0_24px_48px_-20px_rgba(15,15,15,0.18),0_8px_16px_-12px_rgba(15,15,15,0.08)] backdrop-blur-md">
        <p className="flex items-center gap-2 font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-foreground-subtle">
          <span
            aria-hidden
            className="size-1.5 rounded-full bg-[var(--color-primary)]"
          />
          {messages.banner.overline}
        </p>
        <h2 className="mt-3 font-display text-[15px] font-medium leading-snug text-foreground">
          {messages.banner.title}
        </h2>
        <p className="mt-2 text-[13px] leading-relaxed text-foreground-subtle">
          {messages.banner.body}{' '}
          <a
            href={messages.cookiesHref}
            className="font-medium text-foreground underline decoration-border-strong underline-offset-2 transition-colors hover:text-[var(--color-primary)] hover:decoration-[var(--color-primary)]"
          >
            {messages.banner.learnMore}
          </a>
          .
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onCustomize}
            className="inline-flex items-center justify-center rounded-full px-2 py-2 text-[12.5px] font-medium text-foreground-subtle transition-colors hover:text-foreground"
          >
            {messages.banner.customize}
          </button>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={onRejectAll}
              className="inline-flex items-center justify-center rounded-full border border-border bg-white px-3.5 py-2 text-[12.5px] font-medium text-foreground transition-colors hover:bg-background-subtle"
            >
              {messages.banner.rejectShort}
            </button>
            <button
              type="button"
              onClick={onAcceptAll}
              className="inline-flex items-center justify-center rounded-full bg-[var(--color-primary)] px-3.5 py-2 text-[12.5px] font-medium text-white shadow-[0_1px_2px_rgba(15,15,15,0.08)] transition-colors hover:bg-[var(--color-primary-strong,var(--color-primary))]"
            >
              {messages.banner.acceptAll}
            </button>
          </div>
        </div>
      </div>
    </motion.aside>
  );
}

/* --------------------------------------------------------------------------
 * Modal — preferences, with a category list and three actions.
 * ----------------------------------------------------------------------- */

function PreferencesModal({
  messages,
  selection,
  onChange,
  onClose,
  onAcceptAll,
  onRejectAll,
  onSave
}) {
  const dialogRef = useRef(null);

  /* Lock body scroll while the dialog is open. */
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  /* Esc to close + focus trap. */
  useEffect(() => {
    const node = dialogRef.current;
    if (!node) return;

    const focusable = () =>
      Array.from(
        node.querySelectorAll(
          'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
        )
      );

    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusable();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    /* Move initial focus into the dialog for screen readers. */
    const items = focusable();
    if (items.length > 0) items[0].focus({preventScroll: true});

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cc-modal-title"
      aria-describedby="cc-modal-intro"
      className="fixed inset-0 z-[70] flex items-end justify-center overflow-y-auto bg-foreground/40 px-4 py-6 backdrop-blur-[2px] sm:items-center sm:p-8"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="relative flex max-h-[calc(100vh-3rem)] w-full max-w-[620px] flex-col overflow-hidden rounded-[var(--radius-2xl)] border border-border bg-white shadow-[0_32px_64px_-24px_rgba(15,15,15,0.28)]"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={messages.modal.close}
          className="absolute right-4 top-4 z-10 inline-flex size-8 items-center justify-center rounded-full p-0 text-foreground-subtle transition-colors hover:bg-background-subtle hover:text-foreground"
        >
          <svg viewBox="0 0 16 16" className="size-3.5" fill="none" aria-hidden>
            <path
              d="M3 3l10 10M13 3L3 13"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <div className="overflow-y-auto">
          <div className="px-7 pt-8 pb-6 md:px-9 md:pt-10">
            <h2
              id="cc-modal-title"
              className="pr-10 font-display text-[22px] font-medium leading-tight tracking-tight text-foreground md:text-[26px]"
            >
              {messages.modal.title}
            </h2>
            <p
              id="cc-modal-intro"
              className="mt-3 text-[14px] leading-relaxed text-foreground-subtle"
            >
              {messages.modal.intro}
            </p>
            <p className="mt-2 text-[13px] leading-relaxed text-foreground-subtle">
              {messages.modal.privacyNote}{' '}
              <a
                href={messages.privacyHref}
                className="text-foreground underline decoration-border-strong underline-offset-2 transition-colors hover:text-[var(--color-primary)] hover:decoration-[var(--color-primary)]"
              >
                {messages.modal.privacyLink}
              </a>{' '}
              {messages.modal.and}{' '}
              <a
                href={messages.cookiesHref}
                className="text-foreground underline decoration-border-strong underline-offset-2 transition-colors hover:text-[var(--color-primary)] hover:decoration-[var(--color-primary)]"
              >
                {messages.modal.cookiesLink}
              </a>
              .
            </p>
          </div>

          <ul className="px-7 md:px-9">
            {CATEGORIES.map((id) => (
              <CategoryRow
                key={id}
                id={id}
                title={messages.categories[id].title}
                body={messages.categories[id].body}
                checked={selection[id]}
                disabled={id === 'necessary'}
                onChange={onChange}
                alwaysOnLabel={messages.modal.alwaysOn}
              />
            ))}
          </ul>
        </div>

        <div className="flex flex-col gap-3 border-t border-border bg-background-subtle px-7 py-4 sm:flex-row sm:items-center sm:justify-between md:px-9">
          <button
            type="button"
            onClick={onRejectAll}
            className="inline-flex items-center justify-center rounded-full border border-border bg-white px-4 py-2 text-[13px] font-medium text-foreground transition-colors hover:bg-white/60"
          >
            {messages.modal.rejectAll}
          </button>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <button
              type="button"
              onClick={onSave}
              className="inline-flex items-center justify-center rounded-full px-4 py-2 text-[13px] font-medium text-foreground-subtle transition-colors hover:text-foreground"
            >
              {messages.modal.saveSelection}
            </button>
            <button type="button" onClick={onAcceptAll} className="cta-pill-brand">
              {messages.modal.acceptAll}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
 * Public component
 * ----------------------------------------------------------------------- */

export default function CookieConsentV2({locale = 'en'}) {
  const messages = useMemo(() => getMessagesForLocale(locale), [locale]);

  const [hasMounted, setHasMounted] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selection, setSelection] = useState({...DEFAULT_SELECTION});

  /* Read the stored consent on mount. We deliberately defer to
   * `useEffect` to avoid hydration mismatches — the cookie is only
   * readable on the client. */
  useEffect(() => {
    setHasMounted(true);
    const stored = getStoredConsent();

    /* Dev-only diagnostic: surfacing the consent state in the console
     * makes "why isn't the banner showing?" trivial to answer without
     * hand-crafted DevTools snippets. The most common cause is a
     * leftover cookie from a previous test session; the message hints
     * at the reset helper below. */
    if (process.env.NODE_ENV === 'development') {
      if (stored) {
        // eslint-disable-next-line no-console
        console.info(
          '[CookieConsentV2] persisted consent found, banner suppressed.',
          {
            categories: stored.categories,
            acceptedAt: stored.acceptedAt,
          },
          'Run `cookieConsentReset()` in this console to clear and show the banner again.'
        );
      } else {
        // eslint-disable-next-line no-console
        console.info(
          '[CookieConsentV2] no consent stored, banner will appear.'
        );
      }
    }

    if (!stored) {
      setBannerVisible(true);
      return;
    }
    setSelection(selectionFromCategories(stored.categories));
    /* Returning visitor with a persisted choice — upgrade Google
     * Consent Mode from the SSR-time defaults ('denied' for everything
     * that needs consent) to whatever the user previously accepted.
     * Without this the user would have to re-click Accept every visit
     * for analytics tags to fire. */
    syncConsentMode(stored.categories);
  }, []);

  /* Allow other parts of the app (footer link, dev tooling) to open
   * the preferences modal at any time. */
  useEffect(() => {
    if (!hasMounted) return;
    const handler = () => {
      const stored = getStoredConsent();
      setSelection(selectionFromCategories(stored?.categories));
      setModalOpen(true);
      setBannerVisible(false);
    };
    window.addEventListener(OPEN_EVENT, handler);
    return () => window.removeEventListener(OPEN_EVENT, handler);
  }, [hasMounted]);

  /* Expose a few helpers on `window` so we keep the dev contract that
   * the legacy library offered (`window.showCookiePreferences`) and
   * give QA a one-call reset that also reloads the page. */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.showCookiePreferences = () => {
      window.dispatchEvent(new CustomEvent(OPEN_EVENT));
    };
    window.cookieConsentReset = () => {
      clearStoredConsent();
      // eslint-disable-next-line no-console
      console.info('[CookieConsentV2] cookie cleared, reloading…');
      window.setTimeout(() => window.location.reload(), 200);
    };
    return () => {
      delete window.showCookiePreferences;
      delete window.cookieConsentReset;
    };
  }, []);

  const handleAcceptAll = useCallback(() => {
    const isFirst = !getStoredConsent();
    storeAcceptAll({isFirst});
    setSelection(selectionFromCategories(CATEGORIES));
    setBannerVisible(false);
    setModalOpen(false);
  }, []);

  const handleRejectAll = useCallback(() => {
    const isFirst = !getStoredConsent();
    storeRejectAll({isFirst});
    setSelection(selectionFromCategories(['necessary']));
    setBannerVisible(false);
    setModalOpen(false);
  }, []);

  const handleSave = useCallback(() => {
    const isFirst = !getStoredConsent();
    const chosen = CATEGORIES.filter((id) => selection[id]);
    setStoredConsent(chosen, {isFirst});
    setBannerVisible(false);
    setModalOpen(false);
  }, [selection]);

  const handleSelectionChange = useCallback((id, next) => {
    if (id === 'necessary') return;
    setSelection((prev) => ({...prev, [id]: next}));
  }, []);

  const handleOpenModal = useCallback(() => {
    setModalOpen(true);
    setBannerVisible(false);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    /* If the user closes the modal without making a choice, fall back
     * to the banner so we don't silently grant or deny consent. */
    if (!getStoredConsent()) setBannerVisible(true);
  }, []);

  if (!hasMounted) return null;

  /* `data-cc-state` lets the developer/QA introspect the current
   * decision tree from DevTools without spelunking React state:
   *
   *   document.querySelector('[data-cc-state]').dataset.ccState
   *     // => 'banner' | 'modal' | 'persisted' */
  const ccState = bannerVisible ? 'banner' : modalOpen ? 'modal' : 'persisted';

  /* The consent surface is rendered as a sibling to the page tree
   * (so it can sit above any locale layout) which means it is NOT
   * inside the `.v2-root` scope that owns the v2 base resets — no
   * cursor/background/font reset on <button>, no list-style reset on
   * <ul>, etc. We re-establish that scope on a wrapper here so the
   * banner, modal, switches and pill buttons all pick up the same
   * baseline as the rest of the V2 surfaces.
   *
   * The wrapper itself has `display: contents` so it does not introduce
   * a stacking context or background paint of its own; the fixed-
   * positioned banner and modal handle their own placement. */
  return (
    <div
      className="v2-root"
      data-cc-state={ccState}
      style={{display: 'contents'}}
    >
      {bannerVisible && (
        <Banner
          messages={messages}
          onAcceptAll={handleAcceptAll}
          onRejectAll={handleRejectAll}
          onCustomize={handleOpenModal}
        />
      )}
      {modalOpen && (
        <PreferencesModal
          messages={messages}
          selection={selection}
          onChange={handleSelectionChange}
          onClose={handleCloseModal}
          onAcceptAll={handleAcceptAll}
          onRejectAll={handleRejectAll}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

/* Re-export the imperative open helper for callers that prefer a
 * function call over dispatching the DOM event directly. */
export {openPreferences} from './storage';
/* And the storage event names so listeners can subscribe to consent
 * changes (used by `ConditionalAnalytics`). */
export {CONSENT_EVENT, CHANGE_EVENT, OPEN_EVENT};
